// 2) Third-party libraries
import {
    createAction,
    createSelector,
    createSlice,
    PayloadAction,
} from "@reduxjs/toolkit";
import { isEqual, keyBy, merge, uniqWith, values } from "lodash-es";

// 3) Local imports
// 3.1 Constants
import { REPORTS_PAGE } from "../../config/tableAndPageConstants";
import { reportSortByOptions } from "../../constants/reportSortAndFilters";

// 3.3 Project-specific modules � components/types
import { PageData } from "../common/types";
import {
    GetScheduledReportsRequestPayload,
    ScheduledReport,
    ScheduledReportState,
} from "./types";

// 3.4 Utility functions
import { ResponseModalObject } from "../../utils/commonInterfaces";

// 3.5 Redux actions / store
import type { RootState } from "../storeTypes";

const actionPrefix = 'scheduledReport';

// Type-safe initialState!
export const initialState: ScheduledReportState = {
    loading: false,
    errorMessages: [],
    data: [],
    pageData: {
        pageSize: REPORTS_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    filters: {
    },
    tableFilter: undefined,
    sortBy: reportSortByOptions[0].value,
    sortAscending: true,
    activeData: {
        record: {},
        loading: false,
        selectedId: null,
        errorMessages: [],
    }
};

const remittanceAdviceSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        getScheduledReportsForOrganisationRequestAction: (
            state,
            _action: PayloadAction<GetScheduledReportsRequestPayload>
        ) => {
            state.loading = true;
        },
        getScheduledReportsRequestAction: (
            state,
            _action: PayloadAction<GetScheduledReportsRequestPayload>
        ) => {
            state.loading = true;
        },
        getScheduledReportsSuccessAction: (
            state,
            action: PayloadAction<{
                data: ScheduledReport[];
                pageData: PageData;
                mergeData?: boolean;
            }>
        ) => {
            if (action.payload.mergeData === true) {
                state.data = values(
                    merge(
                        keyBy(state.data, 'ScheduleId'),
                        keyBy(action.payload.data, 'ScheduleId')
                    )
                );
            } else if (action.payload.pageData.currentPage === 0) {
                state.data = action.payload.data;
            } else {
                state.data = uniqWith(
                    [...state.data, ...action.payload.data],
                    isEqual
                );
            }

            state.loading = false;
            state.pageData = action.payload.pageData;
            state.errorMessages = initialState.errorMessages;
        },
        getScheduledReportsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = initialState.data;
            state.errorMessages = action.payload;
        },
        updateScheduledReportsTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.tableFilter = action.payload;
        },
        updateScheduledReportsSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetScheduledReportsRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.sortBy = action.payload.sortBy;
            state.sortAscending = action.payload.sortAscending;
        },
        updateScheduledReportsFiltersAction: (
            state,
            action: PayloadAction<GetScheduledReportsRequestPayload['filters']>
        ) => {
            state.filters = action.payload;
        },
        setSelectedScheduledReportIdRequestAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getScheduledReportDataForOrganisationRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    ScheduleId: string;
                    Region: string;
                }>
            ) => {
                state.activeData.loading = true;
            },
            prepare: (
                ScheduleId: string,
                Region: string
            ) => ({
                payload: {
                    ScheduleId,
                    Region
                }
            })
        },
        getScheduledReportDataRequestAction: (
            state,
            _action: PayloadAction<string>
        ) => {
            state.activeData.loading = true;
        },
        getScheduledReportDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.activeData.record = action.payload.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = initialState.activeData.errorMessages;
        },
        getScheduledReportDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.loading = false;
            state.activeData.record = initialState.activeData.record;
            state.activeData.errorMessages = action.payload;
        },
        clearScheduledReportDataAction: (state) => {
            state.activeData = initialState.activeData;
        },
    }
});

const { actions, reducer } = remittanceAdviceSlice;

// Actions
export const {
    getScheduledReportsForOrganisationRequestAction,
    getScheduledReportsRequestAction,
    getScheduledReportsSuccessAction,
    getScheduledReportsErrorAction,
    updateScheduledReportsTableFilterAction,
    updateScheduledReportsSortByAndStateAction,
    updateScheduledReportsFiltersAction,
    setSelectedScheduledReportIdRequestAction,
    getScheduledReportDataForOrganisationRequestAction,
    getScheduledReportDataRequestAction,
    getScheduledReportDataSuccessAction,
    getScheduledReportDataErrorAction,
    clearScheduledReportDataAction
} = actions;

export const createScheduledReportRequest = createAction(
    `${actionPrefix}/createScheduledReportRequest`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const deleteScheuledReportsAction = createAction(
    `${actionPrefix}/deleteScheuledReportsAction`,
    function prepare(
        key: string[],
        CompanyId: string,
        callback?: (response: ResponseModalObject) => void
    ) {
        return {
            payload: { key, CompanyId, callback }
        };
    }
);

// Selectors
export const getScheduledReportState = (state: RootState) => state.scheduledReport;
export const getScheduledReportData = createSelector(
    getScheduledReportState,
    (scheduledReport) => scheduledReport.activeData
);
export const getSelectedScheduledReportId = createSelector(
    getScheduledReportData,
    (activeData) => activeData.selectedId
);

// Reducer
export default reducer;